import { useState, useEffect, useMemo } from 'react'
import { Gauge, Info, RefreshCw, Loader2, Search, X } from 'lucide-react'
import { rrGetWorkload } from '@/actions/rr'

type Status = 'in' | 'break' | 'out'

// enforced=false: shown for tuning, but not yet blocking assignment (RTO/EDD/NDR
// New and NC, counted on work items since 2026-09-25).
interface CapCell { used: number; cap: number; enforced?: boolean }
interface AgentRow {
  user_id: number; name: string; status: Status; since: string
  abc: [CapCell, CapCell, CapCell]
  reorder: [CapCell, CapCell, CapCell]
  rto: [CapCell, CapCell, CapCell]
  edd: [CapCell, CapCell, CapCell]
  ndr: [CapCell, CapCell, CapCell]
}

const METRICS = ['Daily', 'New', 'NC'] as const
const BUCKETS = [
  { key: 'abc', label: 'ABC', chip: 'bg-blue-50 text-blue-700' },
  { key: 'reorder', label: 'Reorder', chip: 'bg-green-50 text-green-700' },
  { key: 'rto', label: 'RTO', chip: 'bg-red-50 text-red-700' },
  { key: 'edd', label: 'EDD', chip: 'bg-sky-50 text-sky-700' },
  { key: 'ndr', label: 'NDR', chip: 'bg-orange-50 text-orange-700' },
] as const

const AV_COLORS = ['#2f9e6b', '#3b82c4', '#b9772e', '#8a5cd1', '#c2557a', '#2aa1a8']
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() }

function lvl(used: number, cap: number): 'ok' | 'warn' | 'full' {
  const pct = cap > 0 ? used / cap : 0
  if (pct >= 1) return 'full'
  if (pct >= 0.7) return 'warn'
  return 'ok'
}

const CAP_COLORS = {
  ok:   { bar: 'bg-green-500', text: 'text-green-700' },
  warn: { bar: 'bg-amber-500', text: 'text-amber-700' },
  full: { bar: 'bg-red-500',   text: 'text-red-600' },
}

function CapBar({ cell }: { cell?: CapCell }) {
  if (!cell) return <div />
  const level = lvl(cell.used, cell.cap)
  const pct = Math.min(100, cell.cap > 0 ? Math.round((cell.used / cell.cap) * 100) : 0)
  const c = CAP_COLORS[level]
  const notEnforced = cell.enforced === false
  return (
    <div
      className={`flex items-center gap-2 ${notEnforced ? 'opacity-60' : ''}`}
      title={notEnforced ? 'Shown for tuning — not blocking assignment yet' : undefined}
    >
      <span className={`text-[11px] font-bold tabular-nums w-12 text-right ${c.text}`}>{cell.used}/{cell.cap}</span>
      <div className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function StatusBadge({ status, since }: { status: Status; since: string }) {
  if (status === 'in') return (
    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />in · {since}
    </div>
  )
  if (status === 'break') return (
    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />break
    </div>
  )
  return <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" />out</div>
}

function AgentCard({ a }: { a: AgentRow }) {
  return (
    <div className="border-b border-gray-100 px-6 py-4 hover:bg-gray-50/60 transition-colors">
      {/* identity row */}
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
        <div className="text-sm font-semibold text-gray-900 truncate">{a.name}</div>
        <StatusBadge status={a.status} since={a.since} />
      </div>

      {/* metric grid: label column on the left, one column per bucket */}
      <div className="grid grid-cols-[40px_repeat(5,1fr)] gap-x-6 gap-y-2">
        {METRICS.map((metric, mi) => (
          <div key={metric} className="contents">
            <div className="flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-400">{metric}</div>
            {BUCKETS.map(b => <CapBar key={b.key} cell={a[b.key][mi]} />)}
          </div>
        ))}
      </div>
    </div>
  )
}

const STATUS_RANK: Record<Status, number> = { in: 0, break: 1, out: 2 }

export default function WorkloadPage() {
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  function load() {
    setLoading(true)
    rrGetWorkload().then(res => {
      setAgents((res?.data ?? []) as AgentRow[])
    }).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  // Filter by name, then sort: in → break → out, alphabetical within each group.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return agents
      .filter(a => !q || a.name.toLowerCase().includes(q))
      .slice()
      .sort((x, y) => STATUS_RANK[x.status] - STATUS_RANK[y.status] || x.name.localeCompare(y.name))
  }, [agents, query])

  const inCount = useMemo(() => agents.filter(a => a.status === 'in').length, [agents])

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Agent Workload</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Live cap usage per agent across all three buckets.
              <span className="ml-1.5 font-semibold text-green-600">{inCount} in</span>
              <span className="text-gray-300"> · </span>
              <span className="text-gray-400">{agents.length} total</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search agent…"
              className="h-9 w-56 pl-8 pr-8 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandColor/30 focus:border-brandColor"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* bucket column header (sticky) */}
      <div className="grid grid-cols-[40px_repeat(5,1fr)] gap-x-6 px-6 py-3 bg-white border-b border-gray-200 text-center flex-shrink-0">
        <span />
        {BUCKETS.map(b => (
          <span key={b.key} className="text-[10px] font-bold uppercase tracking-wider">
            <span className={`px-2 py-0.5 rounded-md ${b.chip}`}>{b.label}</span>
          </span>
        ))}
      </div>

      {/* agent list — fills remaining height */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white">
        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : visible.length === 0
            ? <div className="flex justify-center items-center h-32 text-gray-400 text-sm">{query ? `No agents match “${query}”` : 'No agents found'}</div>
            : visible.map(a => <AgentCard key={a.user_id} a={a} />)
        }
      </div>

      {/* footer legend */}
      <div className="flex items-start gap-2 text-xs text-gray-400 px-6 py-3 border-t border-gray-200 bg-white flex-shrink-0">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
        <span>
          <b className="text-gray-500">Daily</b> resets at midnight ·
          <b className="text-gray-500"> New</b> = undispositioned leads since Jul 1 2026 ·
          <b className="text-gray-500"> NC</b> = "Not Connected" in rolling 7-day window ·
          Amber = ≥70%, red = at cap ·
          <b className="text-gray-500"> RTO / EDD / NDR</b> count the agent's own orders; their faded New / NC bars are shown for tuning and don't block assignment yet.
        </span>
      </div>
    </div>
  )
}
